/**
 * Calculate Preparation Status from Context Data
 *
 * Pure calculation function that derives preparation status from
 * shifts and opening hours data already loaded in the frontend context.
 *
 * NO API calls, NO caching, NO subscriptions.
 * Just calculates from the data you already have.
 */

import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfDay, endOfDay, addDays, addMonths, eachDayOfInterval } from 'date-fns';
import { DayShift, BusinessOpeningHour, BusinessClosedDate } from '@/types';
import { validateDaySchedule, type DayValidationResult, type ValidationStatus, type ShiftData, type OpeningHoursData, type CustomClosedDateOptions } from './schedule-validation';

/**
 * Preparation status for a single day
 */
export interface DayPreparationStatus extends DayValidationResult {
  date: Date;
  dateKey: string;
}

/**
 * Preparation status for a period (week/month)
 */
export interface PeriodPreparationStatus {
  hasShifts: boolean;
  allPrepared: boolean;
  shiftsCount: number;
  daysWithShifts: number;
  preparedDays: number;
  partialDays: number;
  unpreparedDays: number;
}

/**
 * All period statuses
 */
export interface AllPeriodStatuses {
  currentWeek: PeriodPreparationStatus;
  nextWeek: PeriodPreparationStatus;
  currentMonth: PeriodPreparationStatus;
  nextMonth: PeriodPreparationStatus;
}

/**
 * Visible date range for infinite scroll mode
 * When provided, expands calculation to cover all visible days
 */
export interface VisibleDateRange {
  startDate: Date;
  endDate: Date;
}

/**
 * Result from preparation status calculation
 */
export interface PreparationStatusFromContext {
  dayStatuses: Map<string, DayPreparationStatus>;
  periodStatuses: AllPeriodStatuses;
  getDayStatus: (dateKey: string) => DayPreparationStatus | null;
  stats: {
    totalDays: number;
    greenDays: number;
    orangeDays: number;
    redDays: number;
    closedDays: number;
  };
}

// Default empty period status
const emptyPeriodStatus: PeriodPreparationStatus = {
  hasShifts: false,
  allPrepared: false,
  shiftsCount: 0,
  daysWithShifts: 0,
  preparedDays: 0,
  partialDays: 0,
  unpreparedDays: 0
};

/**
 * Convert DayShift (frontend format) to ShiftData (validation format)
 */
const convertToShiftData = (shift: DayShift): ShiftData => ({
  id: shift.id,
  employee_id: shift.employeeId || null,
  start_time: shift.startTime.toISOString(),
  end_time: shift.endTime.toISOString()
});

/**
 * Convert BusinessOpeningHour to OpeningHoursData format
 */
const convertToOpeningHoursData = (oh: BusinessOpeningHour): OpeningHoursData => ({
  id: oh.id,
  day_of_week: oh.day_of_week,
  opening_time: oh.opening_time || '09:00',
  closing_time: oh.closing_time || '17:00',
  is_closed_all_day: oh.is_closed_all_day,
  is_24_hours: oh.is_24_hours
});

/**
 * Get opening hours for a specific day of the week
 */
const getOpeningHoursForDay = (
  openingHours: BusinessOpeningHour[],
  date: Date
): OpeningHoursData | null => {
  const dayNameMap: { [key: number]: string } = {
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday'
  };

  const dayName = dayNameMap[date.getDay()];
  const found = openingHours.find(oh => oh.day_of_week === dayName);

  return found ? convertToOpeningHoursData(found) : null;
};

/**
 * Calculate period status from day validations within a date range
 */
const calculatePeriodStatus = (
  dayStatuses: Map<string, DayPreparationStatus>,
  periodStart: Date,
  periodEnd: Date
): PeriodPreparationStatus => {
  let shiftsCount = 0;
  let daysWithShifts = 0;
  let preparedDays = 0;
  let partialDays = 0;
  let unpreparedDays = 0;

  dayStatuses.forEach(day => {
    if (day.date >= periodStart && day.date <= periodEnd) {
      shiftsCount += day.shiftsCount;

      if (day.hasShifts) {
        daysWithShifts++;

        switch (day.status) {
          case 'green':
            preparedDays++;
            break;
          case 'orange':
            partialDays++;
            break;
          case 'red':
            unpreparedDays++;
            break;
          // 'closed' days don't count
        }
      }
    }
  });

  return {
    hasShifts: shiftsCount > 0,
    allPrepared: daysWithShifts > 0 && preparedDays === daysWithShifts,
    shiftsCount,
    daysWithShifts,
    preparedDays,
    partialDays,
    unpreparedDays
  };
};

/**
 * Main calculation function
 *
 * Takes shifts and opening hours from context and calculates preparation status
 * for all days and periods. Pure function with no side effects.
 *
 * @param shifts - Array of shifts from the context
 * @param openingHours - Array of opening hours for the business
 * @param viewedMonth - Optional: include viewed month in calculation range
 * @param closedDatesMap - Optional: Map of custom closed dates (dateKey -> BusinessClosedDate)
 * @param visibleDateRange - Optional: visible date range for infinite scroll mode
 */
export const calculatePreparationFromContext = (
  shifts: DayShift[],
  openingHours: BusinessOpeningHour[],
  viewedMonth?: Date,
  closedDatesMap?: Map<string, BusinessClosedDate>,
  visibleDateRange?: VisibleDateRange
): PreparationStatusFromContext => {
  const today = new Date();

  // Group shifts by date for validation
  const shiftsByDate = new Map<string, ShiftData[]>();

  shifts.forEach(shift => {
    const dateKey = format(shift.startTime, 'yyyy-MM-dd');
    const existing = shiftsByDate.get(dateKey) || [];
    existing.push(convertToShiftData(shift));
    shiftsByDate.set(dateKey, existing);
  });

  // Calculate date range - include viewed month if different from current/next
  let startDate = startOfMonth(today);
  let endDate = endOfMonth(addMonths(today, 1));

  // Expand range to include viewed month if it's outside default range
  if (viewedMonth) {
    const viewedStart = startOfMonth(viewedMonth);
    const viewedEnd = endOfMonth(viewedMonth);
    if (viewedStart < startDate) startDate = viewedStart;
    if (viewedEnd > endDate) endDate = viewedEnd;
  }

  // Expand range to include visible date range (for infinite scroll mode)
  // This ensures all visible days get their status calculated, even across month boundaries
  if (visibleDateRange) {
    const rangeStart = startOfDay(visibleDateRange.startDate);
    const rangeEnd = endOfDay(visibleDateRange.endDate);
    if (rangeStart < startDate) startDate = rangeStart;
    if (rangeEnd > endDate) endDate = rangeEnd;
  }

  // Generate all days in range
  const allDays = eachDayOfInterval({ start: startDate, end: endDate });

  // Calculate status for each day
  const dayStatuses = new Map<string, DayPreparationStatus>();

  allDays.forEach(date => {
    const dateKey = format(date, 'yyyy-MM-dd');
    const dayShifts = shiftsByDate.get(dateKey) || [];
    const dayOpeningHours = getOpeningHoursForDay(openingHours, date);

    // Check for custom closed date
    const customClosedDate = closedDatesMap?.get(dateKey);
    const customClosedOptions: CustomClosedDateOptions | undefined = customClosedDate ? {
      isCustomClosed: true,
      closureReason: customClosedDate.reason,
      closureType: 'custom_closed_date'
    } : undefined;

    const validation = validateDaySchedule(date, dayShifts, dayOpeningHours, customClosedOptions);

    dayStatuses.set(dateKey, {
      ...validation,
      date: new Date(date),
      dateKey
    });
  });

  // Calculate period boundaries
  // For current week/month: start from TODAY to exclude past days from status calculation
  // Past days should not affect the "preparation" status - only today and future matter
  const currentWeekEnd = endOfWeek(today, { weekStartsOn: 1 });

  const nextWeekStart = startOfWeek(addDays(today, 7), { weekStartsOn: 1 });
  const nextWeekEnd = endOfWeek(addDays(today, 7), { weekStartsOn: 1 });

  const currentMonthEnd = endOfMonth(today);

  const nextMonthStart = startOfMonth(addMonths(today, 1));
  const nextMonthEnd = endOfMonth(addMonths(today, 1));

  // Calculate period statuses
  // Current week/month: start from today (not start of period) to exclude past days
  const periodStatuses: AllPeriodStatuses = {
    currentWeek: calculatePeriodStatus(dayStatuses, today, currentWeekEnd),
    nextWeek: calculatePeriodStatus(dayStatuses, nextWeekStart, nextWeekEnd),
    currentMonth: calculatePeriodStatus(dayStatuses, today, currentMonthEnd),
    nextMonth: calculatePeriodStatus(dayStatuses, nextMonthStart, nextMonthEnd)
  };

  // Calculate overall stats
  let greenDays = 0;
  let orangeDays = 0;
  let redDays = 0;
  let closedDays = 0;

  dayStatuses.forEach(day => {
    switch (day.status) {
      case 'green':
        greenDays++;
        break;
      case 'orange':
        orangeDays++;
        break;
      case 'red':
        redDays++;
        break;
      case 'closed':
        closedDays++;
        break;
    }
  });

  return {
    dayStatuses,
    periodStatuses,
    getDayStatus: (dateKey: string) => dayStatuses.get(dateKey) || null,
    stats: {
      totalDays: dayStatuses.size,
      greenDays,
      orangeDays,
      redDays,
      closedDays
    }
  };
};

/**
 * Get status color class for UI components
 */
export const getPreparationStatusClass = (
  status: ValidationStatus,
  type: 'background' | 'border' | 'text' | 'dot' = 'border'
): string => {
  const colorMap = {
    green: {
      background: 'bg-green-100 dark:bg-green-900/30',
      border: 'border-l-2 border-l-green-500 dark:border-l-green-400',
      text: 'text-green-800 dark:text-green-300',
      dot: 'bg-green-500'
    },
    orange: {
      background: 'bg-orange-100 dark:bg-orange-900/30',
      border: 'border-l-2 border-l-orange-500 dark:border-l-orange-400',
      text: 'text-orange-800 dark:text-orange-300',
      dot: 'bg-orange-500'
    },
    red: {
      background: 'bg-red-100 dark:bg-red-900/30',
      border: 'border-l-2 border-l-red-500 dark:border-l-red-400',
      text: 'text-red-800 dark:text-red-300',
      dot: 'bg-red-500'
    },
    closed: {
      background: 'bg-gray-100 dark:bg-gray-800/30',
      border: 'border-l-0',
      text: 'text-gray-700 dark:text-gray-300',
      dot: 'bg-gray-400'
    }
  };

  return colorMap[status]?.[type] || colorMap.red[type];
};

/**
 * Get period indicator color
 */
export const getPeriodIndicatorColor = (periodStatus: PeriodPreparationStatus): string => {
  if (!periodStatus.hasShifts) {
    return 'bg-red-500';
  }
  if (periodStatus.allPrepared) {
    return 'bg-green-500';
  }
  return 'bg-orange-500';
};
